import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';

/**
 * Date/Time Cell Renderer Component
 *
 * Reusable cell renderer for displaying date and time values.
 * Displays date on the first line and time on the second line.
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'createdAt',
 *   cellRenderer: DateTimeCellRendererComponent
 * }
 * ```
 */
@Component({
  selector: 'app-date-time-cell-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div>
      {{ formattedDate }}<br />{{ formattedTime }}
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateTimeCellRendererComponent implements CellRenderer<any, string | Date> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, string | Date>;

  /**
   * Get formatted date (DD-MM-YYYY)
   * Copied from original table-load-list.component.ts
   */
  get formattedDate(): string {
    return this.formatDate(this.params?.value, false);
  }

  /**
   * Get formatted time (HH:mm:ss)
   * Copied from original table-load-list.component.ts
   */
  get formattedTime(): string {
    return this.formatHour(this.params?.value);
  }

  /**
   * Format date to DD-MM-YYYY format
   * Copied from original table-load-list.component.ts
   *
   * @param date - Date string or Date object
   * @param includeTime - Whether to include time in output
   * @returns Formatted date string
   */
  private formatDate(date: string | Date | null | undefined, includeTime?: boolean): string {
    if (date) {
      const d = new Date(date);
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const year = d.getFullYear();

      if (includeTime) {
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
        const seconds = d.getSeconds().toString().padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
      }
      return `${day}-${month}-${year}`;
    }
    return '--:--:--';
  }

  /**
   * Format time to HH:mm:ss format
   * Copied from original table-load-list.component.ts
   *
   * @param date - Date string or Date object
   * @returns Formatted time string
   */
  private formatHour(date: string | Date | null | undefined): string {
    if (date) {
      const d = new Date(date);
      const hours = d.getHours().toString().padStart(2, '0');
      const minutes = d.getMinutes().toString().padStart(2, '0');
      const seconds = d.getSeconds().toString().padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    }
    return '--:--:--';
  }
}
